import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Insight 001 — Visibility Precedes Improvement™.
 *  A short leadership meditation. Compact by design; large type and
 *  generous whitespace do the visual work.
 * ------------------------------------------------------------------------- */

export default function InsightVisibilityImprovement() {
  return (
    <>
      <SEO
        title="Visibility Precedes Improvement™ | AlignED Insights"
        description="Before an organization can improve, it must first develop consistent visibility into what is actually happening. A short leadership meditation from the AlignED library."
      />

      {/* Article Hero */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="insight-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#52796F] hover:text-[#0A192F] transition-colors"
            data-testid="insight-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Insights
          </Link>
          <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#38BDF8]">
            Idea 001 &middot; A Leadership Principle
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F]">
            Visibility Precedes Improvement<span className="align-super text-2xl">&trade;</span>
          </h1>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed">
            Educational leaders are often asked one question.
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-[#FDFBF7]" data-testid="insight-body">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="prose-editorial text-[#1A1D20] leading-[1.75] text-lg space-y-8">

            <p className="font-serif-display text-3xl sm:text-4xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;How do we improve?&rdquo;
            </p>

            <p>
              It&apos;s an important question.
            </p>

            <p className="font-serif-display text-xl text-[#52796F] leading-snug">
              But it usually comes too soon.
            </p>

            <div className="pt-4 space-y-4">
              <p className="text-xl text-[#1A1D20]">
                Before an organization can improve, it must first develop consistent visibility into what is actually happening.
              </p>
              <ul className="space-y-2 pl-6 border-l-2 border-[#0A192F]/15 text-[#4A5568]">
                <li>Not what was intended.</li>
                <li>Not what was communicated.</li>
                <li>Not what is assumed.</li>
              </ul>
              <p className="font-serif-display text-2xl text-[#0A192F] leading-snug">
                What is actually happening.
              </p>
            </div>

            <div className="pt-6 space-y-5">
              <p className="text-[#1A1D20]">
                Can leaders clearly see whether important work is being completed?
              </p>
              <p className="text-[#1A1D20]">
                Can they identify where consistency begins to drift?
              </p>
              <p className="text-[#1A1D20]">
                Can they recognize successful practices early enough to repeat them elsewhere?
              </p>
            </div>

            <div className="pt-6 space-y-4">
              <p className="text-[#4A5568]">
                Without that visibility, improvement becomes guesswork.
              </p>
              <p className="font-serif-display text-xl text-[#0A192F] leading-snug">
                With it, improvement becomes intentional.
              </p>
            </div>

            <div className="pt-8">
              <p className="text-[#4A5568]">
                At AlignED, we believe a simple leadership truth:
              </p>
              <p className="mt-4 font-serif-display text-3xl sm:text-4xl text-[#0A192F] leading-snug">
                Visibility Precedes Improvement<span className="align-super text-xl">&trade;</span>.
              </p>
            </div>

            <p className="text-[#4A5568] pt-4">
              That principle shapes every Leadership System&trade; we build and every conversation we have with educational leaders.
            </p>
          </div>

          {/* Leadership Reflection */}
          <div className="mt-16 bg-[#0A192F] text-[#FDFBF7] p-10 sm:p-12" data-testid="insight-reflection">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Leadership Reflection</div>
            <p className="mt-6 font-serif-display text-xl sm:text-2xl leading-snug">
              Where in your organization would greater visibility create the greatest opportunity for meaningful improvement?
            </p>
          </div>

          {/* Contextual Invitation */}
          <div className="mt-16 pt-10 border-t border-[#0A192F]/10">
            <p className="text-base text-[#4A5568] leading-relaxed">
              If this principle resonates, the next step is to look for where visibility may be lacking today.
            </p>
            <p className="mt-3 text-base text-[#4A5568] leading-relaxed">
              Take the Alignment Check&trade; to reflect on visibility, ownership, and follow-through across your organization.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={ALIGNMENT_CHECK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
                data-testid="insight-cta-ac"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
              </a>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start sm:self-center hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                data-testid="insight-cta-walkthrough"
              >
                Schedule a Walkthrough <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="mt-16 flex justify-center">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm text-[#52796F] hover:text-[#0A192F] transition-colors border-b border-[#52796F]/40 hover:border-[#0A192F] pb-1"
              data-testid="insight-return-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Return to Insights
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
